/**
 * SNSUrlHider - URL Hiding and Content Manipulation
 * Handles hiding of detected SNS URLs from content
 */

import { Roles } from "../../constants/roles";
import { snsUrlHiderForm } from "./sns-url-hider-form";

interface UrlMatchLike {
    url?: string;
    0?: string;
}

type DetectedLinksMap = Record<string, UrlMatchLike[]>;

class SNSUrlHider {
    public processedElements: WeakSet<object>;

    public constructor() {
        this.processedElements = new WeakSet();
    }

    /**
     * Hide SNS URLs from element content
     */
    public hideUrlsFromElement(element: Element | null, detectedLinks: Partial<DetectedLinksMap> | null): void {
        if (!element || !detectedLinks || Object.keys(detectedLinks).length === 0) {
            return;
        }

        if (this.processedElements.has(element)) {
            return;
        }

        try {
            Object.keys(detectedLinks).forEach((platform) => {
                const platformData = detectedLinks[platform];
                if (!platformData || !Array.isArray(platformData)) return;

                platformData.forEach((match) => {
                    try {
                        const url = match.url || (typeof match[0] === "string" ? match[0] : "");
                        if (!url) return;

                        const escapedUrl = this.escapeRegExp(url);
                        const urlRegex = new RegExp(escapedUrl, "gi");

                        if ("innerHTML" in element && typeof element.innerHTML === "string") {
                            const originalHtml = element.innerHTML;
                            const newHtml = originalHtml.replace(urlRegex, "");
                            if (originalHtml !== newHtml) {
                                element.innerHTML = newHtml;
                            }
                        }

                        if ("value" in element && typeof element.value === "string") {
                            const originalValue = element.value;
                            const newValue = originalValue.replace(urlRegex, "");
                            if (originalValue !== newValue) {
                                element.value = newValue;
                            }
                        }

                        if (element.textContent !== undefined && !("innerHTML" in element && element.innerHTML)) {
                            const originalText = element.textContent || "";
                            const newText = originalText.replace(urlRegex, "");
                            if (originalText !== newText) {
                                element.textContent = newText;
                            }
                        }
                    } catch (error) {
                        console.error("URL hiding failed for element:", error);
                    }
                });
            });

            this.trimElementContent(element);
            this.processedElements.add(element);
        } catch (error) {
            console.error("SNS URL processing failed:", error);
        }
    }

    /**
     * Trim and clean element content after URL removal
     */
    public trimElementContent(element: Element): void {
        try {
            const htmlElement = element instanceof HTMLElement ? element : null;
            const isTitle =
                htmlElement?.matches?.(
                    `[data-role="${Roles.POST_CARD_TITLE}"], [data-role="${Roles.POST_DETAIL_TITLE}"]`,
                ) ?? false;
            const maxLength = isTitle ? 80 : 120;
            const textTarget = htmlElement;

            if (!textTarget) return;

            let textContent = "";
            if (textTarget.textContent) {
                textContent = textTarget.textContent.trim();
            } else if ("innerText" in textTarget && typeof textTarget.innerText === "string") {
                textContent = textTarget.innerText.trim();
            }

            if (!textContent) return;

            if (textContent.length > maxLength) {
                const hasShowMore =
                    snsUrlHiderForm.getShowMoreButton(htmlElement) || htmlElement?.classList.contains("expandable");

                if (!hasShowMore && isTitle) {
                    if (textTarget.textContent !== undefined) {
                        textTarget.textContent = `${textContent.substring(0, maxLength - 3)}...`;
                    }
                } else if (!hasShowMore && htmlElement) {
                    this.preserveLinksAndTruncate(htmlElement, maxLength);
                }
            }
        } catch (error) {
            console.error("Content trimming failed:", error);
        }
    }

    /**
     * Preserve links while truncating content
     */
    public preserveLinksAndTruncate(element: HTMLElement, maxLength: number): void {
        try {
            const links = snsUrlHiderForm.getAnchorElements(element).map((link) => ({
                text: link.textContent || link.innerText,
                href: link.href,
                element: link,
            }));

            if (links.length > 0) {
                const textContent = element.textContent || element.innerText || "";
                if (textContent.length > maxLength) {
                    const truncated = `${textContent.substring(0, maxLength - 3)}...`;

                    if (links.length > 0) {
                        const firstLink = links[0];
                        const linkHtml = `<a href="${firstLink.href}" target="_blank" rel="noopener">${firstLink.text}</a>`;
                        element.innerHTML = `${truncated} ${linkHtml}`;
                    }
                }
            }
        } catch (error) {
            console.error("Link preservation failed:", error);
        }
    }

    /**
     * Escape special regex characters
     */
    public escapeRegExp(value: string): string {
        if (!value || typeof value !== "string") return "";
        return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    }

    /**
     * Reset processed elements (for testing or reinitialization)
     */
    public reset(): void {
        this.processedElements = new WeakSet();
    }
}

export { SNSUrlHider };
